import React from 'react';
import { View, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import Svg, { Rect, Circle, Defs, RadialGradient, Stop } from 'react-native-svg';
import { studentTheme } from '../../../theme/tokens';

interface TwoToneCounterProps {
    topColor?: 'red' | 'yellow' | 'blue' | 'green';
    bottomColor?: 'red' | 'yellow' | 'blue' | 'green';
    size?: number;
    style?: StyleProp<ViewStyle>;
    testID?: string;
}

const colorMap = {
    red: { fill: studentTheme.colors.counter.red, rim: studentTheme.colors.counter.redRing },
    yellow: { fill: studentTheme.colors.counter.yellow, rim: studentTheme.colors.counter.yellowRing },
    blue: { fill: studentTheme.colors.counter.blue, rim: studentTheme.colors.counter.blueRing },
    green: { fill: studentTheme.colors.candy.green, rim: studentTheme.colors.candy.greenBevel },
};

/**
 * TwoToneCounter — Vertical domino/pill tile housing two circular counters.
 * Emulates the physical double-sided two-colour counters standard in UK primary classrooms (White Rose Maths).
 */
export const TwoToneCounter: React.FC<TwoToneCounterProps> = ({
    topColor = 'red',
    bottomColor = 'yellow',
    size = 40,
    style,
    testID = 'two-tone-counter',
}) => {
    const width = size;
    const height = size * 1.75;
    const topTheme = colorMap[topColor];
    const bottomTheme = colorMap[bottomColor];

    return (
        <View testID={testID} style={[styles.container, style]}>
            <Svg width={width} height={height} viewBox="0 0 40 70">
                <Defs>
                    {/* Gloss / Specular Top Counter */}
                    <RadialGradient id="topGlow" cx="16" cy="16" r="14" gradientUnits="userSpaceOnUse">
                        <Stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.5" />
                        <Stop offset="100%" stopColor={topTheme.fill} stopOpacity="1" />
                    </RadialGradient>
                    {/* Gloss / Specular Bottom Counter */}
                    <RadialGradient id="bottomGlow" cx="16" cy="46" r="14" gradientUnits="userSpaceOnUse">
                        <Stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.5" />
                        <Stop offset="100%" stopColor={bottomTheme.fill} stopOpacity="1" />
                    </RadialGradient>
                </Defs>

                {/* Outer Rounded Domino / Tile Housing */}
                <Rect
                    x="2"
                    y="2"
                    width="36"
                    height="66"
                    rx="10"
                    fill="#FFFFFF"
                    stroke="#E2E8F0"
                    strokeWidth="2.5"
                />

                {/* Top Counter Outer Ring */}
                <Circle cx="20" cy="20" r="14" fill={topTheme.rim} />
                {/* Top Counter Core */}
                <Circle cx="20" cy="19" r="12" fill="url(#topGlow)" />

                {/* Bottom Counter Outer Ring */}
                <Circle cx="20" cy="50" r="14" fill={bottomTheme.rim} />
                {/* Bottom Counter Core */}
                <Circle cx="20" cy="49" r="12" fill="url(#bottomGlow)" />
            </Svg>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 3,
        elevation: 2,
    },
});
